import { useWebRTC } from "providers/WebRTCProvider";
import { useLogHistory } from "providers/LogHistoryProvider";
import { useMissionControl } from "providers/navigation/MissionProvider";
import { useNavigation } from "providers/navigation/NavigationProvider";
import { useRobotControl } from "providers/RobotControlProvider";

import JoyStickRow from "components/robot/JoyStickRow";
import MainScreen from "components/control/MainScreen";
import MiniView from "components/layout/MiniView";
import Slider from "components/layout/Slider";
import ButtonsAndViewsGroup from "components/layout/ButtonsAndViewsGroup";

import { ReactComponent as AddViewIcon } from "assets/svgs/streaming/AddView.svg";
import { ReactComponent as MapIcon } from "assets/svgs/navbar/Map.svg";
import { ReactComponent as SlamMapIcon } from "assets/svgs/navbar/slammap.svg";
import { ReactComponent as CameraShowIcon } from "assets/svgs/inspections/CameraShow.svg";
import StreamingSourcePanel from "components/streaming/StreamingSourcePanel";
import PayloadStreamPopup from "components/streaming/PayloadStreamPopup";
import FunctionalButton from "components/layout/FunctionalButton";

import ControlDebugPanel from "components/debug/ControlDebugPanel";

import { useScreen } from "layout/ControlLayout";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useStairs } from "providers/StairsProvider";
import { useGimbalControl } from "providers/GimbalControlProvider";
import { useArmControl } from "providers/ArmControlProvider";

// import { IS_DEV } from "lib/consts/testing";
import {
    HAS_MULTIPLE_VIEWS,
} from "lib/consts/flags";
import { useDeviceFeatureFlags } from "providers/DeviceFeatureFlagsProvider";
import TempMissionPanel from "components/control/TempMissionPanel";
import Stairs from "components/control/Stairs";
import Docking from "components/control/Docking";
import ArmControlPanel from "components/control/ArmControlPanel";
import MissionManagementPanel from "components/navigation/mission/MissionManagementPanel";
import BehaviourControlPanel from "components/navigation/mission/BehaviourControlPanel";
import Portal from "components/layout/Portal";
import NavigateToPoseBanner from "components/navigation/NavigateToPoseBanner";
import useHardwareGps from "hooks/useHardwareGps";
import { useDockingUiVisible } from "lib/hooks/useDockingUiVisible";

// Mini toggle components defined in ControlPage
const MiniMapToggle = ({ onClick, active = false, label }) => (
    <FunctionalButton
        icon={<SlamMapIcon className="h-6 w-6 text-white" />}
        className={`control-button-type1 ${active ? "control-button-active" : "bg-black100"}`}
        hoverText={label}
        onClick={onClick}
    />
);

const OpenStreetMapButton = ({ onClick, active = false, label }) => (
    <FunctionalButton
        icon={<MapIcon className="h-6 w-6 text-white" />}
        className={`control-button-type1 ${active ? "control-button-active" : "bg-black100"}`}
        hoverText={label}
        onClick={onClick}
    />
);

const MiniCameraToggle = ({ onClick }) => (
    <FunctionalButton
        icon={<CameraShowIcon className="w-6" />}
        className="control-button-type1 bg-black100"
        onClick={onClick}
    />
);

const ControlPage = () => {
    const { t } = useTranslation("control");
    const { showControl } = useRobotControl();
    const { showMissionsInControl, showBehaviourInControl } = useMissionControl();
    const { showLogHistoriesInControl } = useLogHistory();
    const { showStreamingSource, fitScreen, setFitScreen, pinnedPayloadStreams, togglePinnedPayloadStream } =
        useWebRTC();
    const { screen, showSecondaryView, setShowSecondaryView, setShowStairsDialog } = useScreen();
    const { flags } = useDeviceFeatureFlags();
    const { HAS_MAP_VIEW, HAS_MISSIONS, HAS_NAV_AUTOMATION } = flags;
    const { photoCaptureActive } = useGimbalControl();
    const { controlArm } = useArmControl();
    const dockingUiVisible = useDockingUiVisible();
    const [speed, setSpeed] = useState(1.0); // Speed slider value in m/s (0.1-4.0)
    const gpsFix = useHardwareGps();
    const [showOpenStreetMapView, setShowOpenStreetMapView] = useState(false);

    const handleOpenStreetMap = () => {
        setShowOpenStreetMapView((prev) => !prev);
    };

    return (
        <div className="page relative">
            <div className="relative h-full w-full">
                {HAS_NAV_AUTOMATION && <NavigateToPoseBanner />}
                <MainScreen />
                <ButtonsAndViewsGroup
                    className="left-5 top-5 z-20"
                    buttonsGroup={[
                        [
                            screen === "map" ? (
                                <>
                                    {HAS_MAP_VIEW && (
                                        <MiniCameraToggle
                                            onClick={() => setShowSecondaryView(!showSecondaryView)}
                                        />
                                    )}
                                    <OpenStreetMapButton
                                        onClick={handleOpenStreetMap}
                                        active={showOpenStreetMapView}
                                        label={t("controlPage.openStreetMap")}
                                    />
                                </>
                            ) : (
                                <>
                                    {HAS_MAP_VIEW && (
                                        <MiniMapToggle
                                            onClick={() => setShowSecondaryView(!showSecondaryView)}
                                            active={showSecondaryView}
                                            label={t("controlPage.slamMap")}
                                        />
                                    )}
                                    <OpenStreetMapButton
                                        onClick={handleOpenStreetMap}
                                        active={showOpenStreetMapView}
                                        label={t("controlPage.openStreetMap")}
                                    />
                                </>
                            ),
                            ...(HAS_MAP_VIEW && HAS_MULTIPLE_VIEWS
                                ? [
                                      <FunctionalButton
                                          key="add-view"
                                          icon={<AddViewIcon className="w-6" />}
                                          className={`control-button-type1`}
                                      />,
                                  ]
                                : []),
                        ],
                    ]}
                    viewsGroup={[
                        ...(HAS_MAP_VIEW
                            ? [
                                  <MiniView
                                      key="secondary-view"
                                      type={screen === "map" ? "stream" : "map"}
                                      visible={showSecondaryView}
                                      onClose={() => setShowSecondaryView(false)}
                                  />,
                              ]
                            : []),
                        <MiniView
                            key="osm-view"
                            type="osm"
                            visible={showOpenStreetMapView}
                            latitude={gpsFix?.latitude}
                            longitude={gpsFix?.longitude}
                            hasFix={Boolean(gpsFix?.hasFix)}
                            onClose={() => setShowOpenStreetMapView(false)}
                        />,
                    ]}
                />
                <ButtonsAndViewsGroup
                    className="bottom-2"
                    buttonsGroup={[
                        [
                            <FunctionalButton
                                title={t("controlPage.fit")}
                                className={`control-button-type2 ${fitScreen && "bg-primary2"}`}
                                onClick={() => setFitScreen(!fitScreen)}
                            />,
                        ],
                    ]}
                />
                {HAS_NAV_AUTOMATION && <Stairs />}
                {HAS_NAV_AUTOMATION && dockingUiVisible && <Docking />}
                <StreamingSourcePanel
                    visible={showStreamingSource}
                    className="absolute inset-0 z-[99] m-auto"
                />
                {Array.isArray(pinnedPayloadStreams) &&
                    pinnedPayloadStreams.map((streamName, index) => (
                        <PayloadStreamPopup
                            key={streamName}
                            streamName={streamName}
                            offsetIndex={index}
                            onClose={() => togglePinnedPayloadStream(streamName)}
                        />
                    ))}
                {/* <TempMissionPanel
                    visible={showMissionsInControl || showLogHistoriesInControl}
                    className="absolute right-5 top-2"
                /> */}
                {HAS_MISSIONS && (
                    <>
                        <MissionManagementPanel
                            visible={showMissionsInControl || showLogHistoriesInControl}
                            className="absolute right-5 top-2 z-[99]"
                        />
                        <BehaviourControlPanel visible={showBehaviourInControl} />
                    </>
                )}
                <ArmControlPanel visible={showControl && controlArm} />
                {/* During photo capture the joystick must sit above the Take Photo dialog,
                    which is Portal'd to #root. z-index only compares within a shared stacking
                    context, so the joystick has to be Portal'd to #root too (fixed-positioned)
                    to beat the dialog's backdrop. */}
                {photoCaptureActive ? (
                    <Portal visible={showControl}>
                        <JoyStickRow
                            visible={showControl}
                            className="fixed bottom-10 left-0 right-0 z-[101]"
                            speedMultiplier={speed}
                        />
                    </Portal>
                ) : (
                    <JoyStickRow
                        visible={showControl}
                        className="absolute bottom-10 z-[98]"
                        speedMultiplier={speed}
                    />
                )}
                <div className="absolute bottom-2 left-0 right-0 m-auto flex w-fit flex-col items-center justify-evenly space-y-5 transition-all">
                    <Slider
                        visible={showControl}
                        title={t("controlPage.speed")}
                        defaultValue={speed}
                        onChange={(value) => setSpeed(value)}
                        lazyUpdate={true}
                        showIndicator={true}
                        unit={t("controlPage.speedUnit")}
                        minValue={0.1}
                        maxValue={4}
                        step={0.1}
                    />
                </div>
                {/* {IS_DEV && <ControlDebugPanel className={"absolute right-4 top-4"} />} */}
            </div>
        </div>
    );
};

export default ControlPage;
